import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from '../hooks/use-i18n'
import AppStoreButton from './AppStoreButton'

const BG = '#0f1623'
const CARD = '#151d2e'
const WARM = '#f59e0b'
const ACCENT = '#38bdf8'

const LandingMarketing: React.FC = () => {
  const { t } = useTranslation()

  const socialStats = [
    { value: t('landing.social.restaurants'), label: t('landing.social.restaurantsLabel') },
    { value: t('landing.social.orders'), label: t('landing.social.ordersLabel') },
    { value: t('landing.social.languages'), label: t('landing.social.languagesLabel') },
    { value: t('landing.social.uptime'), label: t('landing.social.uptimeLabel') },
  ]

  const pillars = [
    { icon: '🧾', title: t('landing.pillar.orders.title'), desc: t('landing.pillar.orders.desc') },
    { icon: '🪑', title: t('landing.pillar.tables.title'), desc: t('landing.pillar.tables.desc') },
    { icon: '🍽️', title: t('landing.pillar.menus.title'), desc: t('landing.pillar.menus.desc') },
    { icon: '📊', title: t('landing.pillar.costs.title'), desc: t('landing.pillar.costs.desc') },
    { icon: '👥', title: t('landing.pillar.staff.title'), desc: t('landing.pillar.staff.desc') },
  ]

  const pricingItems = [
    t('landing.pricingTeaser.item1'),
    t('landing.pricingTeaser.item2'),
    t('landing.pricingTeaser.item3'),
    t('landing.pricingTeaser.item4'),
  ]

  return (
    <div style={{ backgroundColor: BG }}>
      {/* Hero */}
      <section className="pt-4 sm:pt-8 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="min-w-0">
            <p
              className="inline-block text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5 border border-white/15 text-amber-300/90 break-words max-w-full"
              style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
            >
              {t('landing.hero.badge')}
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 break-words hyphens-auto">
              {t('landing.hero.title')}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/70 leading-relaxed mb-6 max-w-xl break-words">
              {t('landing.hero.subtitle')}
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-8 max-w-xl">
              <div className="rounded-xl p-4 border border-amber-500/25" style={{ backgroundColor: CARD }}>
                <span className="text-xl mb-2 block">🏪</span>
                <p className="text-sm font-bold text-amber-300 mb-1">{t('landing.hero.professionals.title')}</p>
                <p className="text-sm text-white/65 leading-relaxed">{t('landing.hero.professionals.desc')}</p>
              </div>
              <div className="rounded-xl p-4 border border-emerald-500/25" style={{ backgroundColor: CARD }}>
                <span className="text-xl mb-2 block">🥗</span>
                <p className="text-sm font-bold text-emerald-300 mb-1">{t('landing.hero.individuals.title')}</p>
                <p className="text-sm text-white/65 leading-relaxed">{t('landing.hero.individuals.desc')}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6">
              <Link
                to="/signup"
                className="inline-flex justify-center px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold text-[#0f1623] transition-transform hover:scale-[1.02] text-center"
                style={{ backgroundColor: WARM }}
              >
                {t('landing.hero.cta.primary')}
              </Link>
              <AppStoreButton variant="cta" />
              <a
                href="#features"
                className="inline-flex justify-center px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base border border-white/25 text-white font-medium hover:bg-white/5 transition-colors text-center"
              >
                {t('landing.hero.cta.secondary')}
              </a>
            </div>
            <p className="text-sm text-white/50">{t('landing.hero.reassurance')}</p>
          </div>

          {/* Dual product mockup */}
          <div className="relative hidden sm:block">
            <div className="grid grid-cols-2 gap-3">
              <div
                className="rounded-2xl border border-white/10 p-4 shadow-2xl"
                style={{ backgroundColor: CARD }}
              >
                <span className="text-xs text-white/40 mb-3 block">{t('landing.mock.dashboard')}</span>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[
                    { label: t('landing.mock.orders'), value: '12', color: ACCENT },
                    { label: t('landing.mock.tables'), value: '8', color: WARM },
                  ].map((m) => (
                    <div key={m.label} className="rounded-lg p-2 border border-white/10" style={{ backgroundColor: '#1a2438' }}>
                      <p className="text-[10px] text-white/50">{m.label}</p>
                      <p className="text-xl font-bold" style={{ color: m.color }}>{m.value}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] font-semibold text-white/60 uppercase tracking-wide mb-1">{t('landing.mock.liveOrders')}</p>
                <p className="text-xs text-white/75">Table 4 · 2 items</p>
              </div>
              <div
                className="rounded-2xl border border-emerald-500/20 p-4 shadow-2xl"
                style={{ backgroundColor: CARD }}
              >
                <span className="text-xs text-emerald-400/80 mb-3 block">{t('landing.mock.nutrition')}</span>
                <p className="text-sm font-bold text-emerald-300 mb-3">🔥 5 {t('landing.mock.streak')}</p>
                {[
                  { label: t('landing.mock.calories'), current: 1240, goal: 2000 },
                  { label: t('landing.mock.protein'), current: 78, goal: 150, unit: 'g' },
                ].map((m) => (
                  <div key={m.label} className="mb-2.5">
                    <div className="flex justify-between text-[10px] text-white/60 mb-1">
                      <span>{m.label}</span>
                      <span>{m.current}/{m.goal}{m.unit ?? ''}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-400" style={{ width: `${Math.round((m.current / m.goal) * 100)}%` }} />
                    </div>
                  </div>
                ))}
                <p className="text-xs text-white/50 mt-3">+ Add meal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-8 border-y border-white/10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {socialStats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-white">{s.value}</p>
              <p className="text-sm text-white/55 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Two audiences */}
      <section className="py-16 lg:py-20">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white text-center mb-10 px-2 break-words">{t('landing.audience.title')}</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl p-8 border border-amber-500/20" style={{ backgroundColor: CARD }}>
            <span className="text-3xl mb-4 block">🏪</span>
            <h3 className="text-xl font-bold text-white mb-3">{t('landing.audience.restaurant.title')}</h3>
            <p className="text-white/65 leading-relaxed">{t('landing.audience.restaurant.desc')}</p>
          </div>
          <div className="rounded-2xl p-8 border border-emerald-500/20" style={{ backgroundColor: CARD }}>
            <span className="text-3xl mb-4 block">🥗</span>
            <h3 className="text-xl font-bold text-white mb-3">{t('landing.audience.howIEat.title')}</h3>
            <p className="text-white/65 leading-relaxed">{t('landing.audience.howIEat.desc')}</p>
          </div>
        </div>
      </section>

      {/* Feature pillars */}
      <section id="features" className="py-16 lg:py-20">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white text-center mb-3 px-2 break-words">{t('landing.pillars.title')}</h2>
        <p className="text-center text-white/60 mb-12 max-w-2xl mx-auto px-2 break-words">{t('landing.pillars.subtitle')}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((p) => (
            <div
              key={p.title}
              className="rounded-2xl p-6 border border-white/10 hover:border-amber-500/30 transition-colors"
              style={{ backgroundColor: CARD }}
            >
              <span className="text-3xl mb-4 block">{p.icon}</span>
              <h3 className="text-lg font-bold text-white mb-2 break-words">{p.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed break-words">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile app */}
      <section className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center rounded-3xl p-8 lg:p-12 border border-white/10" style={{ backgroundColor: CARD }}>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('landing.mobile.title')}</h2>
            <p className="text-white/65 leading-relaxed mb-6">{t('landing.mobile.subtitle')}</p>
            <div className="flex flex-wrap gap-3">
              <span className="px-4 py-2 rounded-lg bg-white/10 text-white/80 text-sm border border-white/10">
                {t('landing.mobile.ios')}
              </span>
              <span className="px-4 py-2 rounded-lg bg-white/10 text-white/80 text-sm border border-white/10">
                {t('landing.mobile.android')}
              </span>
            </div>
          </div>
          <div className="mx-auto w-56 h-96 rounded-[2.5rem] border-4 border-white/20 p-3 shadow-xl" style={{ backgroundColor: '#1a2438' }}>
            <div className="h-full rounded-[2rem] overflow-hidden border border-white/10 flex flex-col">
              <div className="h-8 bg-black/30 flex items-center justify-center">
                <span className="text-[10px] text-white/40">Restoside</span>
              </div>
              <div className="flex-1 p-3 space-y-2">
                <div className="h-16 rounded-lg bg-amber-500/20 border border-amber-500/30" />
                <div className="h-10 rounded-lg bg-white/5" />
                <div className="h-10 rounded-lg bg-white/5" />
                <div className="h-10 rounded-lg bg-white/5" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto text-center rounded-3xl p-8 lg:p-12 border border-white/10" style={{ backgroundColor: CARD }}>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('landing.pricingTeaser.title')}</h2>
          <p className="text-white/60 mb-8">{t('landing.pricingTeaser.subtitle')}</p>
          <p className="text-5xl font-bold text-white mb-1">{t('landing.pricing.plan.price')}</p>
          <p className="text-white/50 text-sm mb-8">{t('landing.pricing.plan.perMonth')}</p>
          <ul className="text-left max-w-md mx-auto space-y-3 mb-8">
            {pricingItems.map((item) => (
              <li key={item} className="flex items-start gap-2 text-white/75 text-sm">
                <span className="text-emerald-400 mt-0.5">✓</span>
                {item}
              </li>
            ))}
          </ul>
          <Link
            to="/pricing"
            className="text-sm font-medium hover:underline"
            style={{ color: ACCENT }}
          >
            {t('landing.pricingTeaser.comparePlans')} →
          </Link>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('landing.footerCta.title')}</h2>
        <p className="text-white/60 mb-8 max-w-lg mx-auto">{t('landing.footerCta.subtitle')}</p>
        <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
          <Link
            to="/signup"
            className="inline-flex justify-center px-10 py-4 rounded-full font-semibold text-[#0f1623]"
            style={{ backgroundColor: WARM }}
          >
            {t('landing.hero.cta.primary')}
          </Link>
          <AppStoreButton variant="cta" className="px-10 py-4" />
        </div>
      </section>
    </div>
  )
}

export default LandingMarketing
